import type { Theme } from '@mui/material/styles';
import type { TickRendererProps } from '@visx/axis';
import type { scaleLinear } from '@visx/scale';
import type { TextProps } from '@visx/text';
import { createElement, ReactNode } from 'react';

/** A linear scale from a domain value to a pixel position. */
export type LinearScale = ReturnType<typeof scaleLinear<number>>;

/** The colours a chart draws its frame in: axes, grid, crosshair, tooltip. */
export interface ChartPalette {
    axisLine: string;
    tickText: string;
    axisLabel: string;
    grid: string;
    crosshair: string;
    tooltipBackground: string;
    tooltipBorder: string;
    tooltipText: string;
}

/** The fixed dark frame of a chart drawn over a black pane. */
export const DARK_CHART_PALETTE: ChartPalette = {
    axisLine: '#555555',
    tickText: '#ffffff',
    axisLabel: '#aaaaaa',
    grid: '#333333',
    crosshair: '#ffffff',
    tooltipBackground: '#000000',
    tooltipBorder: '#000000',
    tooltipText: '#ffffff',
};

/** The frame of a chart drawn on an app surface, following the theme. */
export function chartPaletteFromTheme(theme: Theme): ChartPalette {
    return {
        axisLine: theme.palette.divider,
        tickText: theme.palette.text.secondary,
        axisLabel: theme.palette.text.secondary,
        grid: theme.palette.divider,
        crosshair: theme.palette.text.primary,
        tooltipBackground: theme.palette.background.paper,
        tooltipBorder: theme.palette.divider,
        tooltipText: theme.palette.text.primary,
    };
}

/** Text sizes of an axis: its tick labels and its unit label. */
export interface AxisFontSizes {
    tick: number;
    label: number;
}

/** Where a tick label sits relative to its tick, by the axis it belongs to. */
const TICK_LABEL_PLACEMENT = {
    left: { textAnchor: 'end', dominantBaseline: 'middle', dx: -4, dy: 0 },
    top: { textAnchor: 'middle', dominantBaseline: 'auto', dx: 0, dy: -4 },
    bottom: {
        textAnchor: 'middle',
        dominantBaseline: 'hanging',
        dx: 0,
        dy: 4,
    },
} as const;

/**
 * A tick label as one SVG text element: a formatted value never wraps, so
 * it needs no measurement, and an axis of many ticks paints fast.
 */
function plainTickLabel(props: TickRendererProps): ReactNode {
    const { x, y, formattedValue, dx, dy, ...text } = props;
    return createElement(
        'text',
        { ...text, x: x + Number(dx ?? 0), y: y + Number(dy ?? 0) },
        formattedValue
    );
}

/**
 * The props every axis of a chart shares: line and tick colours from the
 * palette, tick labels centred under or beside their ticks, and the unit
 * label centred along the axis.
 */
export function axisStyle(
    palette: ChartPalette,
    fonts: AxisFontSizes,
    orientation: keyof typeof TICK_LABEL_PLACEMENT
): {
    stroke: string;
    tickStroke: string;
    tickLength: number;
    tickComponent: (props: TickRendererProps) => ReactNode;
    tickLabelProps: Partial<TextProps>;
    labelProps: Partial<TextProps>;
} {
    return {
        stroke: palette.axisLine,
        tickStroke: palette.axisLine,
        tickLength: 5,
        tickComponent: plainTickLabel,
        tickLabelProps: {
            fill: palette.tickText,
            fontSize: fonts.tick,
            ...TICK_LABEL_PLACEMENT[orientation],
        },
        labelProps: {
            fill: palette.axisLabel,
            fontSize: fonts.label,
            textAnchor: 'middle',
        },
    };
}
